import { useState, useEffect, useCallback, useRef } from 'react';

const FAVORITES_STORAGE_KEY = 'text-onl-favorites';

// Helper function to get initial favorites from localStorage
const getStoredFavorites = (): string[] => {
  try {
    const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (error) {
    console.error('Failed to load favorites:', error);
  }
  return [];
};

export function useFavorites() {
  // Use lazy initialization to load from localStorage immediately
  const [favorites, setFavorites] = useState<string[]>(getStoredFavorites);
  const isInitialized = useRef(false);

  // Save favorites to localStorage whenever they change (skip initial mount)
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      return;
    }
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
    } catch (error) {
      console.error('Failed to save favorites:', error);
    }
  }, [favorites]);

  const toggleFavorite = useCallback((slug: string) => {
    setFavorites(prev => {
      if (prev.includes(slug)) {
        return prev.filter(s => s !== slug);
      }
      return [...prev, slug];
    });
  }, []);

  const isFavorite = useCallback((slug: string) => {
    return favorites.includes(slug);
  }, [favorites]);

  const clearFavorites = useCallback(() => {
    setFavorites([]);
  }, []);

  const reorderFavorites = useCallback((newOrder: string[]) => {
    setFavorites(newOrder);
  }, []);

  return {
    favorites,
    toggleFavorite,
    isFavorite,
    clearFavorites,
    reorderFavorites,
    favoritesCount: favorites.length
  };
}
